// Maps a stored (backend) AudienceDefinition — and the derived rows `audience.get` returns beside it —
// into the audienceBuilder's AudienceView for the audience details page. The two models share the
// contract's vocabulary, so almost nothing is translated — `value` is narrowed to the editor's operand
// shapes, and `id` is carried through on leaves AND groups (it is the ONLY join between a predicate
// node and its row; without it `rowsById.get(leaf.id)` never matches and the grounding is dead weight).
//
// `toBackendDefinition` is the inverse, used by the editable pass: the tree hands back a builder
// `AudienceDefinition` on every edit and it must return to the stored contract before it can be saved.
// Only the definition round-trips — `rows` are DERIVED server-side per read and are never sent back.
//
// EXHAUSTIVE BY CONSTRUCTION. Every object below is built by a `projector`, whose field map must name
// EVERY key of the destination type — so adding a field to either model and forgetting it here is a
// COMPILE error, not a field that silently never arrives. A field that exists on ONE side only is
// written out as an explicit `() => undefined` (see `unit` on the distribution control), so the
// asymmetry is a line of code rather than an absence nobody can see.

import type {
  AttributePredicate,
  AttributeType,
  AudienceDefinition as BuilderDefinition,
  AudienceView as BuilderView,
  ConditionValue,
  GroupNode,
  Predicate as BuilderPredicate,
  RawSqlPredicate,
  Row as BuilderRow,
  RowControl as BuilderControl,
  RowDistribution,
  RowPills,
  RowQuantiles,
} from '../components/audienceBuilder/types';
import type {
  AttributePredicate as BackendAttribute,
  AudienceDefinition as BackendDefinition,
  AudienceExpression as BackendExpression,
  AudienceRowWire,
  Predicate as BackendPredicate,
  RawSqlPredicate as BackendRawSql,
  RowControlWire,
} from '../types';
import { andChildrenOf, attributeOf, orChildrenOf, rawSqlOf } from '../types';

/**
 * A TOTAL field map from `Src` to `Dst`: one mapper per destination key, no exceptions. `-?` strips
 * optionality, so a key cannot be left out — that is the whole point. The `| undefined` on the return
 * is what lets an optional destination field say "absent"; {@link projector} then omits the key
 * entirely rather than writing it with an `undefined` value (`expect(row).not.toHaveProperty('type')`
 * passes only when the key is truly absent, and on the backend direction an explicit key would
 * serialise as `"value": null` — which the compiler reads as a real NULL comparison, not as "unset").
 */
type FieldMap<Src, Dst> = { [K in keyof Dst]-?: (src: Src) => Dst[K] | undefined };

/**
 * Build a `Src → Dst` mapping function from a total field map. TWO checks fire on the literal passed
 * in: a MISSING key fails as "Property 'x' is missing", and an EXTRA one as an excess-property error —
 * so neither side of a rename can drift past this seam.
 *
 * The `as Dst` is the one unchecked step and is load-bearing: `project` drops any key whose mapper
 * returns `undefined`, which for a REQUIRED destination field would produce a `Dst` missing it. Every
 * required key below reads a required source field, so read a required field's mapper as a promise the
 * caller keeps, not something the compiler proves.
 */
function projector<Src, Dst>(map: FieldMap<Src, Dst>): (src: Src) => Dst {
  const keys = Object.keys(map) as (keyof Dst & string)[];
  return (src) => {
    const out: Record<string, unknown> = {};
    for (const key of keys) {
      const value = map[key](src);
      if (value !== undefined) out[key] = value;
    }
    return out as Dst;
  };
}

/**
 * The contract's data types as a prototype-free ALLOWLIST. The two models share the vocabulary now, so
 * nothing is translated — this exists purely as a runtime gate: `row.type` is typed `AttributeDataType`,
 * but a value that is not one (a server ahead of this client) would otherwise reach
 * `ATTRIBUTE_OPERATORS[type]`, and on a plain object a key like `'constructor'` resolves to
 * `Object.prototype`'s member — truthy, not an operator list, and a crash inside the clause. An
 * unrecognised type resolves to `undefined` instead, which renders the leaf read-only: the same
 * degradation an unprobed column already gets.
 *
 * `satisfies` keeps it exhaustive — a data type added to the contract must be named here.
 */
const KNOWN_TYPE: Record<string, AttributeType | undefined> = Object.assign(
  Object.create(null) as Record<string, AttributeType | undefined>,
  {
    string: 'string',
    number: 'number',
    boolean: 'boolean',
    timestamp: 'timestamp',
  } satisfies Record<AttributeType, AttributeType>,
);

/** Convert a backend `AudienceDefinition` (+ its derived rows) to the builder's `AudienceView`. */
export function fromBackendDefinition(def: BackendDefinition, rows: AudienceRowWire[] = []): BuilderView {
  return { definition: toBuilderDefinition(def), rows: rows.map(toBuilderRow) };
}

const toBuilderExpression = projector<BackendExpression, BuilderDefinition['expression']>({
  from: (expression) => expression.from,
  idColumn: (expression) => expression.idColumn,
  // CARRIED THROUGH, in BOTH directions — see `toBackendExpression` for why the `() => undefined`
  // idiom is wrong here.
  columns: (expression) => expression.columns,
  // Carried through both ways for the same reason as `columns` (ACT2-783).
  joins: (expression) => expression.joins,
  // And `latestBy` (ACT2-1113): no editor control, so carrying it is what keeps "no control" from
  // meaning "cleared" — dropping it would change which row a multi-row member sends.
  latestBy: (expression) => expression.latestBy,
  // And `rowKey`, for the same reason: dropping it would change which row a multi-row member sends.
  rowKey: (expression) => expression.rowKey,
  where: (expression) => (expression.where === undefined ? undefined : toBuilderPredicate(expression.where)),
});

const toBuilderDefinition = projector<BackendDefinition, BuilderDefinition>({
  expression: (def) => toBuilderExpression(def.expression),
  schemaVersion: (def) => def.schemaVersion,
});

/**
 * The inverse of {@link fromBackendDefinition}: a builder `AudienceDefinition` back to the stored
 * contract, ready for `audience.save`.
 *
 * Deliberately NOT symmetric with the forward direction. Three rules:
 *
 *  1. `id` is carried through on every node it exists on. Dropping it would orphan the leaf from its
 *     Row on the next read (the grounding join is by id) AND change the definition hash for nodes the
 *     human never touched, so an unrelated edit would invalidate the whole audience's count.
 *  2. `sentence` is carried through VERBATIM, never regenerated here. An edited leaf renders its
 *     editor, not its sentence (`AttributePredicateClause` short-circuits to `ReadOnlyClause` only when
 *     `!editable`), so a stale sentence is invisible on this page — and the stored schema requires a
 *     non-blank one, so it cannot simply be dropped. Re-wording belongs to the agent.
 *  3. Rows do NOT travel back. They are derived per read from the expression plus the sketch; the
 *     stored contract is `strictObject` and rejects a definition carrying them.
 */
export const toBackendDefinition = projector<BuilderDefinition, BackendDefinition>({
  expression: (def) => toBackendExpression(def.expression),
  // The builder's `schemaVersion` is a bare `string` (it mirrors the server loosely); the stored
  // contract pins the literal '1'. Anything else is a definition this build cannot have produced.
  schemaVersion: () => '1',
});

const toBackendExpression = projector<BuilderDefinition['expression'], BackendExpression>({
  from: (expression) => expression.from,
  idColumn: (expression) => expression.idColumn,
  // `columns` (ACT2-714) exists on BOTH sides, so it is NOT an `() => undefined` asymmetry — that
  // idiom is for a field one model genuinely lacks. Writing it here would compile, and would drop the
  // list on every UI edit of a metric-view audience: its SQL would revert to `u.*`, which Databricks
  // refuses on a metric view (`METRIC_VIEW_MISSING_MEASURE_FUNCTION`), and `definition_hash` would
  // move on the way out — staling a recorded count nobody asked to invalidate. The editor has no
  // control for this field yet; carrying it through is precisely what makes "no control" mean "left
  // alone" rather than "cleared".
  columns: (expression) => expression.columns,
  // `joins` (ACT2-783) likewise has no editor control yet, and dropping it here would be worse than
  // dropping `columns`: every UI edit of a joined audience would remove `<table>__<column>` from the
  // membership SQL and break any RudderStack sync that maps one. The funnel's `assertStoredJoinsKept`
  // would refuse that save — carrying the list through is what keeps a UI edit from being refused.
  joins: (expression) => expression.joins,
  // Carried through for the same reason as `joins` (ACT2-1113): a UI edit must not change which row a
  // member with several rows sends, nor turn the one-row step off for a joinless audience.
  latestBy: (expression) => expression.latestBy,
  rowKey: (expression) => expression.rowKey,
  where: (expression) => (expression.where === undefined ? undefined : toBackendPredicate(expression.where)),
});

// ── predicates ──────────────────────────────────────────────────────────────
// A predicate is a UNION, so it cannot be projected as one object: the arm is chosen first, then its
// contents are projected. The group arms are `Extract`ed rather than re-declared so a change to either
// `Predicate` union reaches these maps.

type BackendAndNode = Extract<BackendPredicate, { and: BackendPredicate[] }>;
type BackendOrNode = Extract<BackendPredicate, { or: BackendPredicate[] }>;
type BuilderAndNode = Extract<GroupNode, { and: BuilderPredicate[] }>;
type BuilderOrNode = Extract<GroupNode, { or: BuilderPredicate[] }>;

const toBackendAttribute = projector<AttributePredicate, BackendAttribute>({
  id: (attribute) => attribute.id,
  path: (attribute) => attribute.path,
  operator: (attribute) => attribute.operator,
  // `value` is optional on both sides: an operator mid-edit (`btw` before both bounds are typed)
  // legitimately has none, and `null` is how the builder spells "cleared" — both must reach the stored
  // contract as an ABSENT key, never as `"value": null`.
  value: (attribute) => attribute.value ?? undefined,
  sentence: (attribute) => attribute.sentence,
});

const toBackendRawSql = projector<RawSqlPredicate, BackendRawSql>({
  id: (rawSql) => rawSql.id,
  sql: (rawSql) => rawSql.sql,
  sentence: (rawSql) => rawSql.sentence,
});

const toBackendAnd = projector<BuilderAndNode, BackendAndNode>({
  and: (node) => node.and.map(toBackendPredicate),
  id: (node) => node.id,
});

const toBackendOr = projector<BuilderOrNode, BackendOrNode>({
  or: (node) => node.or.map(toBackendPredicate),
  id: (node) => node.id,
});

function toBackendPredicate(node: BuilderPredicate): BackendPredicate {
  if ('attribute' in node) return { attribute: toBackendAttribute(node.attribute) };
  if ('rawSql' in node) return { rawSql: toBackendRawSql(node.rawSql) };
  if ('and' in node) return toBackendAnd(node);
  return toBackendOr(node);
}

const toBuilderAttribute = projector<BackendAttribute, AttributePredicate>({
  id: (attribute) => attribute.id,
  path: (attribute) => attribute.path,
  // `operator` needs no narrowing — the builder's `OperatorValue` IS the contract's operator union.
  // `value` is the one delta: `unknown` on the contract, the editor's operand shapes here.
  operator: (attribute) => attribute.operator,
  value: (attribute) => attribute.value as ConditionValue,
  sentence: (attribute) => attribute.sentence,
});

const toBuilderRawSql = projector<BackendRawSql, RawSqlPredicate>({
  id: (rawSql) => rawSql.id,
  sql: (rawSql) => rawSql.sql,
  sentence: (rawSql) => rawSql.sentence,
});

const toBuilderAnd = projector<BackendAndNode, BuilderAndNode>({
  and: (node) => node.and.map(toBuilderPredicate),
  id: (node) => node.id,
});

const toBuilderOr = projector<BackendOrNode, BuilderOrNode>({
  or: (node) => node.or.map(toBuilderPredicate),
  id: (node) => node.id,
});

function toBuilderPredicate(node: BackendPredicate): BuilderPredicate {
  // Arms are read by VALUE, not key presence — the rule `predicate-tree.ts` exists to state. The casts
  // are justified by the value check immediately before each, and going through the projectors keeps
  // the group maps exhaustive.
  const attribute = attributeOf(node);
  if (attribute) return { attribute: toBuilderAttribute(attribute) };
  const rawSql = rawSqlOf(node);
  if (rawSql) return { rawSql: toBuilderRawSql(rawSql) };
  if (andChildrenOf<BackendPredicate>(node)) return toBuilderAnd(node as BackendAndNode);
  if (orChildrenOf<BackendPredicate>(node)) return toBuilderOr(node as BackendOrNode);
  return { and: [] }; // unreachable for a valid predicate; keeps the function total
}

// ── rows ────────────────────────────────────────────────────────────────────

type WirePills = Extract<RowControlWire, { kind: 'pills' }>;
type WireQuantiles = Extract<RowControlWire, { kind: 'quantiles' }>;
type WireDistribution = Extract<RowControlWire, { kind: 'distribution' }>;

const toBuilderRow = projector<AudienceRowWire, BuilderRow>({
  id: (row) => row.id,
  type: (row) => (row.type === undefined ? undefined : KNOWN_TYPE[row.type]),
  sentence: (row) => row.sentence,
  count: (row) => row.count,
  control: (row) => (row.control === undefined ? undefined : toBuilderControl(row.control)),
  label: (row) => row.label,
  // Narrowed at runtime for the same reason `KNOWN_TYPE` is: the type says these are the only two, but
  // a pod ahead of this SPA could send a third, and a provenance we cannot name must never render as
  // though it were a measurement.
  labelSource: (row) => (row.labelSource === 'agent' || row.labelSource === 'digest' ? row.labelSource : undefined),
  bounds: (row) => row.bounds,
  nonNullFraction: (row) => row.nonNullFraction,
});

const toBuilderPills = projector<WirePills, RowPills>({
  kind: () => 'pills',
  // Carried by reference: `PillValue` IS the contract's pill type (`ContractRowPills['values'][number]`),
  // so there is no second declaration to drift from — a rename reaches both sides at once.
  values: (control) => control.values,
  total: (control) => control.total,
  totalAccuracy: (control) => control.totalAccuracy,
  // Carried UNCOALESCED, like every other measurement: absent means NOT MEASURED, never zero, and the
  // two travel together or not at all.
  valueCoverage: (control) => control.valueCoverage,
  valueCount: (control) => control.valueCount,
});

const toBuilderQuantiles = projector<WireQuantiles, RowQuantiles>({
  kind: () => 'quantiles',
  estimated: (control) => control.estimated,
  quantiles: (control) => control.quantiles,
  min: (control) => control.min,
  max: (control) => control.max,
  nonNullFraction: (control) => control.nonNullFraction,
  step: (control) => control.step,
  total: (control) => control.total,
  totalAccuracy: (control) => control.totalAccuracy,
});

const toBuilderDistribution = projector<WireDistribution, RowDistribution>({
  kind: () => 'distribution',
  bins: (control) => control.bins,
  // Every optional field is carried UNCOALESCED. `stats` is legitimately absent when the server
  // declined a non-monotonic ladder, `step` when the column is not integer-native, and `estimated`
  // whenever the bins were measured rather than interpolated — an `?? {}` / `?? false` here would
  // publish each of those absences as a fact.
  // `encoding` says which numeric encoding the bins are in. Dropping it silently disarms the guard
  // that stops a days-ago ladder rendering under the calendar domain — the panel reads an absent
  // encoding as "unverifiable, render anyway", so the field going missing looks like nothing at all.
  encoding: (control) => control.encoding,
  // Which space the bins are equal-width in. Carried UNCOALESCED like the rest: absent means a server
  // that predates the tag, which the axis renders linear — a default written here would claim a
  // measurement the server never made.
  scale: (control) => control.scale,
  stats: (control) => control.stats,
  total: (control) => control.total,
  step: (control) => control.step,
  // BUILDER-ONLY field: the wire has no `unit`, so there is nothing to carry. Written out rather than
  // omitted so the asymmetry is visible here — the moment the server grows one, this line is where it
  // gets wired, and until then nobody has to wonder whether it was forgotten.
  unit: () => undefined,
  estimated: (control) => control.estimated,
});

function toBuilderControl(control: RowControlWire): BuilderControl | undefined {
  if (control.kind === 'pills') return toBuilderPills(control);
  if (control.kind === 'quantiles') return toBuilderQuantiles(control);
  if (control.kind === 'distribution') return toBuilderDistribution(control);
  // Exhaustive on purpose: a further contract control kind must fail to COMPILE here rather than fall
  // through and be re-shaped as one of the three above. At runtime — reachable only from a server ahead
  // of this client — the row renders with NO control, which is the same graceful degradation an
  // unprobed column already gets, rather than a half-mapped one.
  control satisfies never;
  return undefined;
}
